import * as extensions from "@afriannotate/extensions";

// Readers for tags an installed extension adds: given an element of the
// setup, the task fields it reads.
export const configFieldReaders: ((el: Element) => { field: string; kind?: string; list?: boolean }[])[] =
  (extensions as any).configFields ?? [];

/**
 * Extract the set of $varname keys the label config XML references.
 * Reads every ``value="$x"`` and ``valueList="$x"`` attribute on
 * object tags and strips the leading ``$``. Used by:
 *
 *   - the Import page to warn when uploaded data is missing one of
 *     these fields (so the user finds out before clicking Save
 *     instead of seeing LSF complain that the task data has no
 *     ``$image`` / ``$text``);
 *   - the CreateProject Save-button gate so the wizard refuses to
 *     finalise a project whose data doesn't match its config.
 *
 * Returns ``[]`` when the config is empty / unparseable. The empty
 * default is what we want — no required keys means no validation
 * gate fires.
 */
export const requiredKeysFromConfig = (configXml: string | undefined | null): string[] => {
  if (!configXml) return [];
  try {
    const doc = new DOMParser().parseFromString(configXml, "application/xml");
    const flatten = (el: Element): Element[] => {
      const out: Element[] = [];
      for (const child of Array.from(el.children || [])) {
        out.push(child);
        if (child.children?.length) out.push(...flatten(child));
      }
      return out;
    };
    const keys = new Set<string>();
    for (const tag of flatten(doc.documentElement)) {
      const v = tag.getAttribute("value") || tag.getAttribute("valueList");
      if (v?.startsWith("$")) keys.add(v.slice(1));
      // Tags an extension adds may read task fields in their own way.
      for (const read of configFieldReaders) {
        for (const found of read(tag) ?? []) if (found?.field) keys.add(found.field);
      }
    }
    return Array.from(keys);
  } catch {
    return [];
  }
};
